import React, { useState, useEffect } from 'react';
import { Check, X, Crown, Zap, Star, CreditCard, Calendar, AlertCircle } from 'lucide-react';

const Subscriptions = () => {
    const [currentPlan, setCurrentPlan] = useState('free');
    const [billingCycle, setBillingCycle] = useState('monthly'); // monthly, yearly
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadSubscriptionData();
    }, []);

    const loadSubscriptionData = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentPlan(user.subscription_tier?.toLowerCase() || 'free');
        
        // Mock payment history
        setPaymentHistory([
            { id: 1, date: '2026-01-15', amount: 499, plan: 'Premium', status: 'completed' },
            { id: 2, date: '2025-12-15', amount: 299, plan: 'Core', status: 'completed' },
            { id: 3, date: '2025-11-15', amount: 299, plan: 'Core', status: 'completed' }
        ]);
    };

    const plans = [
        {
            id: 'free',
            name: 'Free',
            icon: <Star size={32} color="#95A5A6" />,
            monthlyPrice: 0,
            yearlyPrice: 0,
            color: '#95A5A6',
            features: [
                { text: '5 practice sessions per week', included: true },
                { text: 'Basic speech analysis', included: true },
                { text: 'Limited AI feedback', included: true },
                { text: 'Community support', included: true },
                { text: 'Advanced analytics', included: false },
                { text: 'Personalized learning paths', included: false },
                { text: 'Certificate access', included: false },
                { text: 'Priority support', included: false }
            ]
        },
        {
            id: 'trust',
            name: 'Trust',
            icon: <Zap size={32} color="#3498DB" />,
            monthlyPrice: 199,
            yearlyPrice: 1999,
            color: '#3498DB',
            popular: false,
            features: [
                { text: '15 practice sessions per week', included: true },
                { text: 'Advanced speech analysis', included: true },
                { text: 'AI-powered feedback', included: true },
                { text: 'Progress tracking', included: true },
                { text: 'Advanced analytics', included: true },
                { text: 'Personalized learning paths', included: false },
                { text: 'Certificate access', included: false },
                { text: '24/7 email support', included: true }
            ]
        },
        {
            id: 'core',
            name: 'Core',
            icon: <Crown size={32} color="#FF9800" />,
            monthlyPrice: 299,
            yearlyPrice: 2999,
            color: '#FF9800',
            popular: true,
            features: [
                { text: '30 practice sessions per week', included: true },
                { text: 'Premium speech analysis', included: true },
                { text: 'Advanced AI coaching', included: true },
                { text: 'Detailed performance analytics', included: true },
                { text: 'Personalized learning paths', included: true },
                { text: 'Certificate access', included: true },
                { text: 'Pronunciation training', included: true },
                { text: 'Priority support', included: true }
            ]
        },
        {
            id: 'premium',
            name: 'Premium',
            icon: <Crown size={32} color="#9C27B0" />,
            monthlyPrice: 499,
            yearlyPrice: 4999,
            color: '#9C27B0',
            popular: false,
            features: [
                { text: 'Unlimited practice sessions', included: true },
                { text: 'Expert-level analysis', included: true },
                { text: 'AI personal tutor', included: true },
                { text: 'Complete analytics suite', included: true },
                { text: 'Custom learning plans', included: true },
                { text: 'All certificates', included: true },
                { text: '1-on-1 coaching sessions', included: true },
                { text: 'VIP support', included: true }
            ]
        }
    ];

    const handleUpgrade = (planId) => {
        setLoading(true);
        // Simulate upgrade process
        setTimeout(() => {
            alert(`Upgrading to ${planId}...`);
            setLoading(false);
        }, 1000);
    };

    const handleCancelSubscription = () => {
        if (window.confirm('Are you sure you want to cancel your subscription?')) {
            alert('Subscription cancelled. You will retain access until the end of your billing period.');
        }
    };

    const getPrice = (plan) => {
        if (billingCycle === 'yearly') {
            return {
                amount: plan.yearlyPrice,
                perMonth: Math.round(plan.yearlyPrice / 12),
                savings: ((plan.monthlyPrice * 12) - plan.yearlyPrice)
            };
        }
        return { amount: plan.monthlyPrice };
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Subscriptions & Billing</h1>
                <p style={styles.subtitle}>Choose the plan that fits your learning goals</p>
            </div>

            {currentPlan !== 'free' && (
                <div style={styles.currentPlanBanner}>
                    <div style={styles.bannerContent}>
                        <Crown size={24} color="#27B05B" />
                        <div>
                            <strong>Current Plan:</strong> {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                            <span style={styles.bannerRenewal}>Renews on March 15, 2026</span>
                        </div>
                    </div>
                    <button onClick={handleCancelSubscription} style={styles.cancelButton}>
                        Cancel Subscription
                    </button>
                </div>
            )}

            <div style={styles.billingToggle}>
                <span style={{...styles.billingLabel, ...(billingCycle === 'monthly' ? styles.billingLabelActive : {})}}>
                    Monthly
                </span>
                <label style={styles.switch}>
                    <input
                        type="checkbox"
                        checked={billingCycle === 'yearly'}
                        onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                    />
                    <span style={styles.slider}></span>
                </label>
                <span style={{...styles.billingLabel, ...(billingCycle === 'yearly' ? styles.billingLabelActive : {})}}>
                    Yearly <span style={styles.savingsBadge}>Save 16%</span>
                </span>
            </div>

            <div style={styles.plansGrid}>
                {plans.map(plan => {
                    const price = getPrice(plan);
                    const isCurrent = plan.id === currentPlan;

                    return (
                        <div 
                            key={plan.id}
                            style={{
                                ...styles.planCard,
                                ...(isCurrent ? styles.planCardCurrent : {}),
                                ...(plan.popular ? styles.planCardPopular : {})
                            }}
                        >
                            {plan.popular && <div style={styles.popularBadge}>Most Popular</div>}
                            
                            <div style={styles.planHeader}>
                                <div style={{...styles.planIcon, backgroundColor: `${plan.color}20`}}>
                                    {plan.icon}
                                </div>
                                <h3 style={styles.planName}>{plan.name}</h3>
                            </div>

                            <div style={styles.planPricing}>
                                {plan.monthlyPrice === 0 ? (
                                    <div style={styles.freePrice}>Free Forever</div>
                                ) : (
                                    <>
                                        <div style={styles.priceAmount}>
                                            ₹{price.amount}
                                            <span style={styles.pricePeriod}>/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                                        </div>
                                        {billingCycle === 'yearly' && (
                                            <div style={styles.priceDetails}>
                                                ₹{price.perMonth}/month • Save ₹{price.savings}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div style={styles.featuresList}>
                                {plan.features.map((feature, idx) => (
                                    <div key={idx} style={styles.featureItem}>
                                        {feature.included ? (
                                            <Check size={18} color="#27B05B" />
                                        ) : (
                                            <X size={18} color="#E0E0E0" />
                                        )}
                                        <span style={{
                                            ...styles.featureText,
                                            color: feature.included ? '#2C3E50' : '#999'
                                        }}>
                                            {feature.text}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {isCurrent ? (
                                <button style={{...styles.planButton, ...styles.planButtonCurrent}}>
                                    Current Plan
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleUpgrade(plan.id)}
                                    disabled={loading}
                                    style={{...styles.planButton, backgroundColor: plan.color}}
                                >
                                    {currentPlan === 'free' ? 'Get Started' : 
                                     plan.monthlyPrice === 0 ? 'Downgrade' : 'Upgrade'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {paymentHistory.length > 0 && (
                <div style={styles.historySection}>
                    <h2 style={styles.sectionTitle}>
                        <CreditCard size={24} />
                        Payment History
                    </h2>
                    
                    <div style={styles.historyTable}>
                        <div style={styles.tableHeader}>
                            <div style={styles.tableCell}>Date</div>
                            <div style={styles.tableCell}>Plan</div>
                            <div style={styles.tableCell}>Amount</div>
                            <div style={styles.tableCell}>Status</div>
                        </div>
                        
                        {paymentHistory.map(payment => (
                            <div key={payment.id} style={styles.tableRow}>
                                <div style={styles.tableCell}>
                                    <Calendar size={16} color="#666" />
                                    {new Date(payment.date).toLocaleDateString()}
                                </div>
                                <div style={styles.tableCell}>{payment.plan}</div>
                                <div style={styles.tableCell}>₹{payment.amount}</div>
                                <div style={styles.tableCell}>
                                    <span style={{
                                        ...styles.statusBadge,
                                        backgroundColor: payment.status === 'completed' ? '#E8F5E9' : '#FFF3E0',
                                        color: payment.status === 'completed' ? '#27B05B' : '#FF9800'
                                    }}>
                                        {payment.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={styles.faqSection}>
                <AlertCircle size={20} color="#3498DB" />
                <div>
                    <strong>Need help?</strong> Contact our support team at{' '}
                    <a href="https://mail.google.com/mail/?view=cm&fs=1&to=nandhakumar@arivunest.com" target="_blank" rel="noopener noreferrer" style={styles.link}>
                        nandhakumar@arivunest.com
                    </a>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    header: {
        textAlign: 'center',
        marginBottom: '40px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    subtitle: {
        fontSize: '16px',
        color: '#666',
        marginTop: '10px'
    },
    currentPlanBanner: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        backgroundColor: '#E8F5E9',
        borderRadius: '12px',
        marginBottom: '30px',
        border: '2px solid #27B05B'
    },
    bannerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        fontSize: '16px',
        color: '#2C3E50'
    },
    bannerRenewal: {
        display: 'block',
        fontSize: '13px',
        color: '#666',
        marginTop: '4px'
    },
    cancelButton: {
        padding: '10px 20px',
        backgroundColor: 'transparent',
        color: '#E74C3C',
        border: '2px solid #E74C3C',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    billingToggle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '40px'
    },
    billingLabel: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#999',
        transition: 'color 0.3s ease'
    },
    billingLabelActive: {
        color: '#27B05B'
    },
    switch: {
        position: 'relative',
        display: 'inline-block',
        width: '50px',
        height: '24px'
    },
    slider: {
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ccc',
        transition: '0.4s',
        borderRadius: '24px'
    },
    savingsBadge: {
        padding: '4px 8px',
        backgroundColor: '#27B05B',
        color: 'white',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '700',
        marginLeft: '8px'
    },
    plansGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '25px',
        marginBottom: '50px'
    },
    planCard: {
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    planCardPopular: {
        border: '3px solid #FF9800',
        transform: 'scale(1.05)'
    },
    planCardCurrent: {
        border: '3px solid #27B05B'
    },
    popularBadge: {
        position: 'absolute',
        top: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '6px 16px',
        backgroundColor: '#FF9800',
        color: 'white',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '700'
    },
    planHeader: {
        textAlign: 'center',
        marginBottom: '20px'
    },
    planIcon: {
        width: '70px',
        height: '70px',
        margin: '0 auto 15px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    planName: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    planPricing: {
        textAlign: 'center',
        marginBottom: '25px',
        paddingBottom: '25px',
        borderBottom: '2px solid #F0F0F0'
    },
    freePrice: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#27B05B'
    },
    priceAmount: {
        fontSize: '42px',
        fontWeight: '700',
        color: '#2C3E50'
    },
    pricePeriod: {
        fontSize: '18px',
        color: '#999'
    },
    priceDetails: {
        fontSize: '14px',
        color: '#27B05B',
        marginTop: '8px',
        fontWeight: '600'
    },
    featuresList: {
        marginBottom: '25px'
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 0'
    },
    featureText: {
        fontSize: '14px'
    },
    planButton: {
        width: '100%',
        padding: '14px',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    planButtonCurrent: {
        backgroundColor: '#95A5A6',
        cursor: 'not-allowed'
    },
    historySection: {
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '30px'
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '22px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '20px'
    },
    historyTable: {
        width: '100%'
    },
    tableHeader: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        padding: '15px',
        backgroundColor: '#F5F5F5',
        borderRadius: '8px',
        fontWeight: '600',
        color: '#666',
        fontSize: '14px'
    },
    tableRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        padding: '15px',
        borderBottom: '1px solid #E0E0E0',
        alignItems: 'center'
    },
    tableCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#2C3E50'
    },
    statusBadge: {
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'capitalize'
    },
    faqSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: '20px',
        backgroundColor: '#E3F2FD',
        borderRadius: '12px',
        color: '#2C3E50'
    },
    link: {
        color: '#3498DB',
        fontWeight: '600',
        textDecoration: 'none'
    }
};

export default Subscriptions;
